import type { GrowthPoint } from './growth-model';

export type GrowthBarKey =
  'signups' | 'newAppUsers' | 'newPaid' | 'recordedRevenueUsd';

const definitions: Array<{
  key: GrowthBarKey;
  label: string;
  color: string;
  fill: string;
  currency?: 'USD';
}> = [
  {
    key: 'newPaid',
    label: 'New paid',
    color: 'bg-positive',
    fill: 'fill-positive',
  },
  {
    key: 'recordedRevenueUsd',
    label: 'Payments · USD',
    color: 'bg-positive',
    fill: 'fill-positive',
    currency: 'USD',
  },
];

/** Exactly one bar series: paid starts by default, or recorded USD payments. */
export const growthBars = (
  points: GrowthPoint[],
  visible: Record<GrowthBarKey, boolean>,
) =>
  definitions
    .filter(
      (series) =>
        (visible.recordedRevenueUsd
          ? series.key === 'recordedRevenueUsd'
          : visible[series.key]) &&
        points.some((point) => point[series.key] !== null),
    )
    .map((row) => ({
      ...row,
      maximum: Math.max(1, ...points.map((point) => point[row.key] ?? 0)),
    }));

/** No mark for zero or unknown; the tooltip preserves the distinction. */
export const growthBarHeight = (
  value: number | null,
  maximum: number,
  height: number,
): number | null =>
  value === null
    ? null
    : value <= 0
      ? 0
      : Math.min(height, Math.max(1, (value / maximum) * height));

/** A dot marks activity, not its count or its position on the traffic scale. */
export const growthUserMarker = (
  point: GrowthPoint,
  visible: { signups: boolean; newAppUsers: boolean },
) => {
  const signup = visible.signups && (point.signups ?? 0) > 0;
  const appUser = visible.newAppUsers && (point.newAppUsers ?? 0) > 0;
  return signup ? 'signup' : appUser ? 'appUser' : null;
};
